import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { previewBoardPointerBlockSx } from '../common/clickableCardSx';
import ClickableCardLink from '../common/ClickableCardLink';
import PreviewBoardPlaceholder from '../common/PreviewBoardPlaceholder';
import { touchNavButtonSx } from '../common/touchTargetSx';
import { useFittedBoardWidth } from '../common/useFittedBoardWidth';
import {
  previewBoardTapCaptureContainerSx,
  previewBoardTapCaptureOverlaySx,
} from '../common/previewBoardShell';
import ReviewCardPreviewBoard from '../review/ReviewCardPreviewBoard';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import {
  getEntitlementErrorMessage,
  isEntitlementError,
} from '../subscription/entitlementError';
import {
  type DashboardPuzzlePeek,
  fetchEasyAndHardPuzzles,
  fetchPuzzleStats,
  normalizeDashboardPlayerRating,
} from './puzzlePeekApi';
import {
  puzzlePlayPath,
  type PuzzleDeepLinkBand,
} from '../puzzles/puzzleUrl';
import DashboardSection from './DashboardSection';
import {
  DASHBOARD_PUZZLE_MAX_BOARD_WIDTH,
  dashboardCardSecondarySx,
  dashboardInteractiveCardSx,
} from './dashboardMuiStyles';

const puzzlePeekCardSx = {
  ...dashboardInteractiveCardSx,
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const;

const puzzlePeekSolveCtaSx = {
  display: 'inline-block',
  color: 'primary.main',
  fontSize: { xs: '0.8125rem', xxl: '0.875rem', xxxl: '0.9375rem' },
} as const;

const useDashboardPuzzleBoardWidth = () => {
  const theme = useTheme();
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const maxBoardWidth = isUltraWide
    ? DASHBOARD_PUZZLE_MAX_BOARD_WIDTH.xxxl
    : isWideDesktop
      ? DASHBOARD_PUZZLE_MAX_BOARD_WIDTH.xxl
      : DASHBOARD_PUZZLE_MAX_BOARD_WIDTH.xs;
  return useFittedBoardWidth(maxBoardWidth);
};

const PuzzlePeekBoardFrame = ({
  board,
  containerRef,
  interactive,
  children,
}: {
  board: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  interactive: boolean;
  children: React.ReactNode;
}) => (
  <Box
    ref={containerRef}
    sx={{
      ...previewBoardTapCaptureContainerSx,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 1.5, xxl: 1.75, xxxl: 2 },
      ...(interactive ? previewBoardPointerBlockSx : { pointerEvents: 'none' }),
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {board}
      {interactive && <Box aria-hidden sx={previewBoardTapCaptureOverlaySx} />}
    </Box>
    <Box sx={{ width: '100%', minWidth: 0, textAlign: 'center' }}>{children}</Box>
  </Box>
);

const PuzzlePeekPlaceholderCard = ({ label }: { label: string }) => {
  const { containerRef, boardWidth } = useDashboardPuzzleBoardWidth();

  return (
    <Box aria-hidden sx={puzzlePeekCardSx}>
      <PuzzlePeekBoardFrame
        board={<PreviewBoardPlaceholder boardWidth={boardWidth} />}
        containerRef={containerRef}
        interactive={false}
      >
        <Chip size="small" label={label} sx={{ mb: 1 }} />
        <Typography sx={{ ...dashboardCardSecondarySx, mb: 0.5, visibility: 'hidden' }}>
          Rating 0000
        </Typography>
        <Typography variant="button" sx={{ ...puzzlePeekSolveCtaSx, visibility: 'hidden' }}>
          Solve →
        </Typography>
      </PuzzlePeekBoardFrame>
    </Box>
  );
};

const PuzzlePeekCard = ({
  label,
  band,
  puzzle,
}: {
  label: string;
  band: PuzzleDeepLinkBand;
  puzzle: DashboardPuzzlePeek;
}) => {
  const { containerRef, boardWidth } = useDashboardPuzzleBoardWidth();

  return (
    <ClickableCardLink
      to={puzzlePlayPath(puzzle.id, band)}
      ariaLabel={`${label} puzzle, rating ${puzzle.rating != null ? Math.round(puzzle.rating) : 'unknown'}`}
      sx={puzzlePeekCardSx}
    >
      <PuzzlePeekBoardFrame
        board={
          <ReviewCardPreviewBoard
            staticFen={puzzle.fen}
            animate={false}
            boardWidth={boardWidth}
          />
        }
        containerRef={containerRef}
        interactive
      >
        <Chip size="small" label={label} sx={{ mb: 1 }} />
        {puzzle.rating != null && (
          <Typography sx={{ ...dashboardCardSecondarySx, mb: 0.5 }}>
            Rating {Math.round(puzzle.rating)}
          </Typography>
        )}
        <Typography variant="button" sx={puzzlePeekSolveCtaSx}>
          Solve →
        </Typography>
      </PuzzlePeekBoardFrame>
    </ClickableCardLink>
  );
};

export const PuzzleSpotlightSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [easyPuzzle, setEasyPuzzle] = useState<DashboardPuzzlePeek | null>(null);
  const [hardPuzzle, setHardPuzzle] = useState<DashboardPuzzlePeek | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage('');
      setLimitMessage('');
      try {
        const stats = await fetchPuzzleStats();
        const rating = normalizeDashboardPlayerRating(stats.puzzle_elo);
        const { easy, hard, entitlementError } = await fetchEasyAndHardPuzzles(
          rating,
          stats.ratingBounds,
        );
        if (cancelled) {
          return;
        }
        setEasyPuzzle(easy);
        setHardPuzzle(hard);
        if (entitlementError) {
          setLimitMessage(
            getEntitlementErrorMessage(entitlementError) ??
              'Daily puzzle limit reached.',
          );
        } else if (!easy && !hard) {
          setErrorMessage('No puzzles found for your rating.');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const entitlementMessage = getEntitlementErrorMessage(error);
        if (entitlementMessage || isEntitlementError(error)) {
          setLimitMessage(
            entitlementMessage ?? 'Daily puzzle limit reached.',
          );
          setEasyPuzzle(null);
          setHardPuzzle(null);
        } else {
          setErrorMessage(
            getApiErrorMessage(error, { action: 'Failed to load puzzles' }),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasPuzzle = easyPuzzle || hardPuzzle;

  return (
    <DashboardSection
      title="Puzzles"
      action={
        <Button
          size="small"
          onClick={() => navigate('/puzzles')}
          sx={touchNavButtonSx}
        >
          All puzzles
        </Button>
      }
    >
      {loading && (
        <Box
          aria-busy
          aria-label="Loading puzzles"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, xxl: 2.5, xxxl: 3 },
          }}
        >
          <PuzzlePeekPlaceholderCard label="Easy" />
          <PuzzlePeekPlaceholderCard label="Hard" />
        </Box>
      )}

      {!loading && limitMessage && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/subscribe">
              Upgrade
            </Button>
          }
        >
          {limitMessage}
        </Alert>
      )}

      {!loading && errorMessage && (
        <Alert severity="warning">{errorMessage}</Alert>
      )}

      {!loading && hasPuzzle && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, xxl: 2.5, xxxl: 3 },
          }}
        >
          {easyPuzzle && (
            <PuzzlePeekCard label="Easy" band="easy" puzzle={easyPuzzle} />
          )}
          {hardPuzzle && (
            <PuzzlePeekCard label="Hard" band="hard" puzzle={hardPuzzle} />
          )}
        </Box>
      )}
    </DashboardSection>
  );
};

export default PuzzleSpotlightSection;
